import { useState, useEffect, useMemo } from 'react';
import { useNetworkingOptimized, Message } from './useNetworkingOptimized';
import { useAuth } from '@/contexts/AuthContext';
import { Conversation, ConversationMessage } from '@/types/conversation';
import { supabase } from '@/integrations/supabase/client';
import { globalCache } from '@/utils/cache';

export const useConversations = () => {
  const { user } = useAuth();
  const { messages, connections, loading, sendMessage, markMessageAsRead, deleteMessage, deleteConversation, fetchMessages } = useNetworkingOptimized();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [missingProfiles, setMissingProfiles] = useState<Map<string, any>>(new Map());

  // Nettoyer les caches corrompus au changement d'utilisateur
  useEffect(() => {
    if (!user?.id) return;
    
    console.log('🧹 Nettoyage cache conversations pour:', user.id);
    
    // Invalider les caches pour forcer un rechargement frais
    globalCache.invalidate(`messages_${user.id}`);
    globalCache.invalidate(`connections_${user.id}`);
    
    // Forcer le rechargement des messages
    fetchMessages();
  }, [user?.id]);

  // Load missing profiles from club_members
  useEffect(() => {
    if (!user || !messages.length) return;

    const missingIds = new Set<string>();
    
    messages.forEach((message) => {
      const participantId = message.sender_id === user.id ? message.recipient_id : message.sender_id;
      
      // ✅ VALIDATION: Filtrer les participantId invalides
      if (!participantId || participantId === 'undefined' || participantId.trim() === '') {
        console.warn('⚠️ [useConversations] participantId invalide détecté:', {
          messageId: message.id,
          senderId: message.sender_id,
          recipientId: message.recipient_id
        });
        return; // Skip ce message
      }
      
      if (!connections.find(conn => conn.id === participantId) && !missingProfiles.has(participantId)) {
        missingIds.add(participantId);
      }
    });

    if (missingIds.size === 0) return;

    const loadMissingProfiles = async () => {
      // ✅ FILTRAGE: Vérifier que tous les IDs sont valides avant la requête
      const validIds = Array.from(missingIds).filter(id => 
        id && 
        id !== 'undefined' && 
        id !== 'null' && 
        id.trim() !== '' &&
        id.length === 36 // UUID valide
      );

      if (validIds.length === 0) {
        console.warn('⚠️ [useConversations] Aucun ID valide à charger');
        return;
      }

      console.log('🔍 [useConversations] Chargement de', validIds.length, 'profils manquants');

      const { data, error } = await supabase
        .from('club_members')
        .select('user_id, firstname, lastname, name, image')
        .in('user_id', validIds);

      if (error) {
        console.error('❌ [useConversations] Erreur chargement profils:', error);
        return;
      }

      if (data) {
        console.log('✅ [useConversations] Profils chargés:', data.length);
        const newProfiles = new Map(missingProfiles);
        data.forEach(profile => {
          newProfiles.set(profile.user_id, {
            id: profile.user_id,
            firstname: profile.firstname || profile.name?.split(' ')[0] || '',
            lastname: profile.lastname || profile.name?.split(' ').slice(1).join(' ') || '',
            avatar_url: profile.image
          });
        });
        setMissingProfiles(newProfiles);
      }
    };

    loadMissingProfiles();
  }, [messages, connections, user]);

  // Group messages into conversations
  const conversations = useMemo(() => {
    if (!user || !messages.length) return [];

    const conversationMap = new Map<string, Conversation>();

    messages.forEach((message) => {
      // Determine the other participant (not the current user)
      const participantId = message.sender_id === user.id ? message.recipient_id : message.sender_id;
      
      // Find participant details from connections or missing profiles
      let participant = connections.find(conn => conn.id === participantId);
      if (!participant) {
        participant = missingProfiles.get(participantId);
      }
      
      const participantName = participant ? 
        `${participant.firstname} ${participant.lastname}`.trim() || 'Utilisateur' : 
        'Utilisateur inconnu';

      // Create conversation message
      const conversationMessage: ConversationMessage = {
        id: message.id,
        sender_id: message.sender_id,
        recipient_id: message.recipient_id,
        content: message.content,
        subject: message.subject,
        is_read: message.is_read,
        created_at: message.created_at,
        senderName: message.sender_id === user.id ? 'Vous' : participantName,
        senderAvatar: message.sender_id === user.id ? undefined : participant?.avatar_url,
        isOwnMessage: message.sender_id === user.id,
        messageType: (message.message_type || 'text') as any,
        sharedResource: message.shared_resource_type ? {
          type: message.shared_resource_type as any,
          id: message.shared_resource_id || '',
          data: message.shared_resource_data
        } : undefined
      };

      if (conversationMap.has(participantId)) {
        const conversation = conversationMap.get(participantId)!;
        conversation.messages.push(conversationMessage);
        
        // Update last message if this one is more recent
        if (new Date(message.created_at) > new Date(conversation.lastMessage.timestamp)) {
          conversation.lastMessage = {
            content: message.content,
            timestamp: message.created_at,
            sender_id: message.sender_id,
            isRead: message.is_read
          };
        }

        // Count unread messages
        if (!message.is_read && message.recipient_id === user.id) {
          conversation.unreadCount++;
        }
      } else {
        // Create new conversation
        conversationMap.set(participantId, {
          id: participantId,
          participantId,
          participantName,
          participantAvatar: participant?.avatar_url,
          lastMessage: {
            content: message.content,
            timestamp: message.created_at,
            sender_id: message.sender_id,
            isRead: message.is_read
          },
          unreadCount: (!message.is_read && message.recipient_id === user.id) ? 1 : 0,
          messages: [conversationMessage]
        });
      }
    });

    // Sort messages within each conversation by date
    conversationMap.forEach(conversation => {
      // VALIDATION: Filtrer les messages avec timestamps invalides
      const originalLength = conversation.messages.length;
      conversation.messages = conversation.messages.filter(msg => {
        try {
          const date = new Date(msg.created_at);
          const isValid = !isNaN(date.getTime());
          if (!isValid) {
            console.warn('⚠️ Message avec timestamp invalide filtré:', {
              id: msg.id,
              timestamp: msg.created_at,
              conversationId: conversation.id
            });
          }
          return isValid;
        } catch (error) {
          console.error('❌ Erreur validation timestamp:', error);
          return false;
        }
      });
      
      if (originalLength !== conversation.messages.length) {
        console.warn(`🗑️ ${originalLength - conversation.messages.length} message(s) invalide(s) supprimé(s)`);
      }
      
      conversation.messages.sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });

    // Convert to array and sort by last message date
    return Array.from(conversationMap.values()).sort((a, b) => 
      new Date(b.lastMessage.timestamp).getTime() - new Date(a.lastMessage.timestamp).getTime()
    );
  }, [messages, connections, user]);

  const selectedConversation = useMemo(() => {
    return conversations.find(conv => conv.id === selectedConversationId) || null;
  }, [conversations, selectedConversationId]);

  const sendMessageInConversation = async (
    recipientId: string, 
    content: string, 
    subject?: string,
    messageType?: string,
    sharedResourceType?: string,
    sharedResourceId?: string,
    sharedResourceData?: any
  ) => {
    const success = await sendMessage(
      recipientId, 
      content, 
      subject, 
      undefined,
      messageType,
      sharedResourceType,
      sharedResourceId,
      sharedResourceData
    );
    if (success && !selectedConversationId) {
      setSelectedConversationId(recipientId);
    }
    return success;
  };

  const markConversationAsRead = async (conversationId: string) => {
    const conversation = conversations.find(conv => conv.id === conversationId);
    if (!conversation || !user) return;

    // Mark all unread messages in this conversation as read
    const unreadMessages = conversation.messages.filter(
      msg => !msg.is_read && msg.recipient_id === user.id
    );

    if (unreadMessages.length === 0) return;

    // Mark all messages as read in a single atomic operation
    const messageIds = unreadMessages.map(msg => msg.id);
    
    await supabase
      .from('messages')
      .update({ is_read: true })
      .in('id', messageIds);

    // Invalidate cache and notify all components
    globalCache.invalidate(`messages_${user.id}`);
    window.dispatchEvent(new CustomEvent('networking-messages-updated'));
  };

  const deleteMessageFromConversation = async (messageId: string) => {
    const success = await deleteMessage(messageId);
    return success;
  };

  const deleteEntireConversation = async (conversationId: string) => {
    const success = await deleteConversation(conversationId);
    if (success && selectedConversationId === conversationId) {
      setSelectedConversationId(null);
    }
    return success;
  };

  return {
    conversations,
    selectedConversation,
    selectedConversationId,
    setSelectedConversationId,
    sendMessageInConversation,
    markConversationAsRead,
    deleteMessageFromConversation,
    deleteEntireConversation,
    loading
  };
};